import { ActionFactoryParams } from '../../../utils/ControlledComponent/ControlledComponent.types';
import { CalendarState } from '../controller';
import { CalendarContext } from '../../../utils/context/contextFactory';
import {
  createInitializeWidgetAction,
  InitializeWidget,
} from './initializeWidget/initializeWidget';
import {
  createOnDateSelectedAction,
  OnDateSelected,
} from './onDateSelected/onDateSelected';
import {
  createGoToNextAvailableDateAction,
  GoToNextAvailableDate,
} from './goToNextAvailableDate/goToNextAvailableDate';
import { createSetSelectedMonthAction } from './setSelectedMonth/setSelectedMonth';
import {
  createOnTimeSelectedAction,
  OnTimeSelected,
} from './onTimeSelected/onTimeSelected';
import {
  createOnBookingPreferenceOptionSelectedAction,
  OnBookingPreferenceOptionSelected,
} from './onBookingPreferenceOptionSelected/onBookingPreferenceOptionSelected';
import {
  createOnTimezoneSelectedAction,
  OnTimezoneSelected,
} from './onTimezoneSelected/onTimezoneSelected';
import { createOnNextAction, OnNext } from './onNext/onNext';
import { createRefetchPlatformDataAction } from './refetchPlatformData/refetchPlatformData';
import {
  createOnClearSelectedBookingPreferencesAction,
  OnClearSelectedBookingPreferences,
} from './onClearSelectedBookingPreferences/onClearSelectedBookingPreferences';
import { createSetSelectedTimeAction } from './setSelectedTime/setSelectedTime';
import {
  createNavigateToMembersAreaAction,
  NavigateToMembersArea,
} from './navigateToMembersArea/navigateToMembersArea';
import {
  createOnMonthSelectedAction,
  OnMonthSelected,
} from './onMonthSelected/onMonthSelected';
import {
  createOnDialogCloseAction,
  OnDialogClose,
} from './onDialogClose/onDialogClose';
import {
  createOnDialogConfirmAction,
  OnDialogConfirm,
} from './onDialogConfirm/onDialogConfirm';
import {
  createOnFilterChanged,
  OnFilterChanged,
} from './onFilterChanged/onFilterChanged';
import { createSetSelectedDateAction } from './setSelectedDate/setSelectedDate';
import {
  createOnSlotsNotificationCtaClick,
  OnSlotsNotificationCtaClick,
} from './onSlotsNotificationCtaClick/onSlotsNotificationCtaClick';
import {
  createOnToastCloseAction,
  OnToastClose,
} from './onToastClose/onToastClose';
import {
  createOnElementClicked,
  OnElementClicked,
} from './onElementClicked/onElementClicked';
import { createSetFocusedElementAction } from './setFocusedElement/setFocusedElement';
import { createCloseDialogAction } from './closeDialog/closeDialog';
import { createAddErrorAction } from './addError/addError';
import { createRemoveErrorAction } from './removeError/removeError';
import { createOnRangeSetAction, OnRangeSet } from './onRangeSet/onRangeSet';
import { createSetSelectedRangeAction } from './setSelectedRange/setSelectedRange';
import {
  createOnLoginClickAction,
  OnLoginClick,
} from './onLoginClick/onLoginClick';
import {
  createOnUserLoggedInAction,
  OnUserLoggedIn,
} from './onUserLoggedIn/onUserLoggedIn';
import { createNavigateToNextPageAction } from './navigateToNextPage/navigateToNextPage';
import {
  createOnSlotSelectedAction,
  OnSlotSelected,
} from './onSlotSelected/onSlotSelected';
import {
  createOnServiceVariantsOptionSelected,
  OnServiceVariantsOptionSelected,
} from './onServiceVariantsOptionSelected/onServiceVariantsOptionSelected';
import {
  AutoSelectTime,
  createAutoSelectTime,
} from './autoSelectTime/autoSelectTime';
import {
  createOnServiceChange,
  OnServiceChange,
} from './onServiceChange/onServiceChange';
import {
  createOpenServicesPreferencesModalAction,
  OpenServicesPreferencesModal,
} from './openServicesPreferencesModal/openServicesPreferencesModal';
import {
  createOnPreferencesModalServiceSelectedAction,
  OnPreferencesModalServiceSelected,
} from './onPreferencesModalServiceSelected/onPreferencesModalServiceSelected';
import {
  createOnServicesPreferencesConfirm,
  OnServicesPreferencesConfirm,
} from './onServicesPreferencesConfirm/onServicesPreferencesConfirm';
import {
  createOnElementLoaded,
  OnElementLoaded,
} from './onElementLoaded/onElementLoaded';

export type CalendarActions = {
  initializeWidget: InitializeWidget;
  onDateSelected: OnDateSelected;
  goToNextAvailableDate: GoToNextAvailableDate;
  onMonthSelected: OnMonthSelected;
  onRangeSet: OnRangeSet;
  onTimeSelected: OnTimeSelected;
  onBookingPreferenceOptionSelected: OnBookingPreferenceOptionSelected;
  onNext: OnNext;
  onSlotSelected: OnSlotSelected;
  onClearSelectedBookingPreferences: OnClearSelectedBookingPreferences;
  onTimezoneSelected: OnTimezoneSelected;
  navigateToMembersArea: NavigateToMembersArea;
  onDialogClose: OnDialogClose;
  onDialogConfirm: OnDialogConfirm;
  onFilterChanged: OnFilterChanged;
  onSlotsNotificationCtaClick: OnSlotsNotificationCtaClick;
  onToastClose: OnToastClose;
  onElementClicked: OnElementClicked;
  onLoginClick: OnLoginClick;
  onUserLoggedIn: OnUserLoggedIn;
  onServiceVariantsOptionSelected: OnServiceVariantsOptionSelected;
  autoSelectTime: AutoSelectTime;
  onServiceChange: OnServiceChange;
  openServicesPreferencesModal: OpenServicesPreferencesModal;
  onPreferencesModalServiceSelected: OnPreferencesModalServiceSelected;
  onServicesPreferencesConfirm: OnServicesPreferencesConfirm;
  onElementLoaded: OnElementLoaded;
};

export const createCalendarActions = (
  actionFactoryParams: ActionFactoryParams<CalendarState, CalendarContext>,
): CalendarActions => {
  const onServiceVariantsOptionSelected =
    createOnServiceVariantsOptionSelected(actionFactoryParams);
  const addError = createAddErrorAction(actionFactoryParams);
  const removeError = createRemoveErrorAction(actionFactoryParams);
  const setFocusedElement = createSetFocusedElementAction(actionFactoryParams);
  const setSelectedMonth = createSetSelectedMonthAction(actionFactoryParams);
  const setSelectedTime = createSetSelectedTimeAction(
    actionFactoryParams,
    addError,
    onServiceVariantsOptionSelected,
  );
  const autoSelectTime = createAutoSelectTime(
    actionFactoryParams,
    setSelectedTime,
  );
  const setSelectedDate = createSetSelectedDateAction(
    actionFactoryParams,
    addError,
    setSelectedTime,
  );
  const setSelectedRange = createSetSelectedRangeAction(
    actionFactoryParams,
    addError,
    setSelectedTime,
  );
  const refetchPlatformData = createRefetchPlatformDataAction(
    actionFactoryParams,
    setSelectedDate,
    setSelectedMonth,
    setSelectedRange,
    autoSelectTime,
  );
  const closeDialog = createCloseDialogAction(actionFactoryParams);
  const onDialogClose = createOnDialogCloseAction(
    actionFactoryParams,
    closeDialog,
  );
  const goToNextAvailableDate = createGoToNextAvailableDateAction(
    actionFactoryParams,
    setSelectedDate,
    setSelectedMonth,
    setSelectedRange,
    addError,
    autoSelectTime,
  );
  const navigateToNextPage =
    createNavigateToNextPageAction(actionFactoryParams);

  return {
    initializeWidget: createInitializeWidgetAction(
      actionFactoryParams,
      setSelectedDate,
      setSelectedMonth,
      setSelectedRange,
      addError,
      autoSelectTime,
    ),
    onDateSelected: createOnDateSelectedAction(
      actionFactoryParams,
      setSelectedDate,
      setFocusedElement,
      autoSelectTime,
    ),
    goToNextAvailableDate,
    onMonthSelected: createOnMonthSelectedAction(
      actionFactoryParams,
      setSelectedMonth,
    ),
    onRangeSet: createOnRangeSetAction(actionFactoryParams, setSelectedRange),
    onTimeSelected: createOnTimeSelectedAction(
      actionFactoryParams,
      setSelectedTime,
      setFocusedElement,
    ),
    onBookingPreferenceOptionSelected:
      createOnBookingPreferenceOptionSelectedAction(
        actionFactoryParams,
        removeError,
        onServiceVariantsOptionSelected,
      ),
    onNext: createOnNextAction(
      actionFactoryParams,
      addError,
      setFocusedElement,
      navigateToNextPage,
    ),
    onSlotSelected: createOnSlotSelectedAction(
      actionFactoryParams,
      navigateToNextPage,
    ),
    onClearSelectedBookingPreferences:
      createOnClearSelectedBookingPreferencesAction(actionFactoryParams),
    onTimezoneSelected: createOnTimezoneSelectedAction(
      actionFactoryParams,
      refetchPlatformData,
      setSelectedTime,
    ),
    navigateToMembersArea:
      createNavigateToMembersAreaAction(actionFactoryParams),
    onFilterChanged: createOnFilterChanged(
      actionFactoryParams,
      refetchPlatformData,
    ),
    onDialogClose,
    onDialogConfirm: createOnDialogConfirmAction(
      actionFactoryParams,
      onDialogClose,
      addError,
    ),
    onSlotsNotificationCtaClick: createOnSlotsNotificationCtaClick(
      goToNextAvailableDate,
    ),
    onToastClose: createOnToastCloseAction(actionFactoryParams, removeError),
    onElementClicked: createOnElementClicked(actionFactoryParams),
    onLoginClick: createOnLoginClickAction(actionFactoryParams),
    onUserLoggedIn: createOnUserLoggedInAction(actionFactoryParams, addError),
    onServiceVariantsOptionSelected,
    autoSelectTime,
    onServiceChange: createOnServiceChange(actionFactoryParams),
    openServicesPreferencesModal:
      createOpenServicesPreferencesModalAction(actionFactoryParams),
    onPreferencesModalServiceSelected:
      createOnPreferencesModalServiceSelectedAction(actionFactoryParams),
    onServicesPreferencesConfirm: createOnServicesPreferencesConfirm(
      actionFactoryParams,
      closeDialog,
    ),
    onElementLoaded: createOnElementLoaded(actionFactoryParams),
  };
};
